import differenceInYears from 'date-fns/differenceInYears';
import React from 'react';
import { Control } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { getCountryCode } from '@/modules/common/store/selectors';
import { IHelloForm } from '@/modules/onboarding/model/helloStep';
import { useGenderOptions } from '@/shared/hooks/useGenderOptions';
import { useLicensingAuthorityTypes } from '@/shared/hooks/useLicensingAuthorityTypes';
import { CountryCode } from '@/shared/models/country-code';
import { BottomButtons } from '@/shared/view/BottomButtons';
import { Section } from '@/shared/view/Section';
import { Button } from '@src-common/view/adapters/Button';
import { DateField } from '@src-common/view/adapters/reactHookForm/DateField';
import { SelectField } from '@src-common/view/adapters/reactHookForm/SelectField';
import { TextField } from '@src-common/view/adapters/reactHookForm/TextField';
import {
  DocsList,
  IDocsListItem,
  HandleDocListItemClick,
} from '@src-common/view/components/DocsList';

import { messages } from './messages';
import s from './styles.module.css';

interface IDriverViewProps {
  control: Control<IHelloForm>;
  docs: IDocsListItem[];
  onDocListItemClick: HandleDocListItemClick;
  isSubmitting?: boolean;
}

const DriverForm: React.FC<IDriverViewProps> = ({
  isSubmitting,
  control,
  docs,
  onDocListItemClick,
}) => {
  const intl = useIntl();
  const links = useExternalLinks();

  const countryCode = useSelector(getCountryCode);

  const licensingAuthorityTypes = useLicensingAuthorityTypes();
  const genderOptions = useGenderOptions(countryCode);

  return (
    <>
      <Section>
        <DateField
          name="birthday"
          title={intl.formatMessage(messages.birthdayTitle)}
          control={control}
          isRequired={true}
          rules={{
            validate: value =>
              value && differenceInYears(new Date(), value as Date) < 18
                ? intl.formatMessage(messages.birthdayMinAgeError)
                : undefined,
          }}
        />

        <SelectField
          name="gender"
          title={intl.formatMessage(messages.genderTitle)}
          control={control}
          items={genderOptions}
          isRequired={true}
        />

        {countryCode === CountryCode.GreatBritain && (
          <>
            <SelectField
              name="licensingAuthority"
              title={intl.formatMessage(messages.gbLicensingAuthorityTitle)}
              description={intl.formatMessage(messages.gbLicensingAuthorityDesc, {
                a: (text: React.ReactNode) => (
                  <a href={links.licensingAuthorities} target="_blank" rel="noreferrer">
                    {text}
                  </a>
                ),
              })}
              control={control}
              items={licensingAuthorityTypes}
            />
            <TextField
              name="licenseNumber"
              title={intl.formatMessage(messages.gbDriverLicenseTitle)}
              placeholder="12345678"
              description={intl.formatMessage(messages.gbDriverLicenseDesc)}
              control={control}
              isRequired={true}
            />
            <TextField
              name="ukNationalInsurance"
              title={intl.formatMessage(messages.gbInsuranceTitle)}
              placeholder="AB123456C"
              isRequired={true}
              rules={{
                pattern: {
                  value: /^\w{2}\d{6}\w$/i,
                  message: intl.formatMessage(messages.gbInsurancePatternError),
                },
              }}
              control={control}
            />
          </>
        )}

        {countryCode === CountryCode.France && (
          <TextField
            name="licenseNumber"
            title={intl.formatMessage(messages.frVtcTitle)}
            placeholder="AAAA00000000"
            description={intl.formatMessage(messages.frVtcDesc)}
            control={control}
            isRequired={true}
          />
        )}

        {countryCode === CountryCode.UnitedStates && (
          <TextField
            name="licenseNumber"
            title={intl.formatMessage(messages.usDriverLicenseTitle)}
            control={control}
            isRequired={true}
          />
        )}

        {countryCode === CountryCode.Russia && (
          <TextField
            name="personalInn"
            title={intl.formatMessage(messages.ruInnTitle)}
            isRequired={true}
            description={intl.formatMessage(messages.ruInnDesc, {
              link: (message: React.ReactNode) => (
                <a target="_blank" rel="noreferrer" href={links.findInn}>
                  {message}
                </a>
              ),
            })}
            control={control}
          />
        )}

        <TextField
          title={intl.formatMessage(messages.addressTitle)}
          name="homeAddressLine1"
          isRequired={true}
          placeholder={intl.formatMessage(messages.addressLine1Title)}
          control={control}
        />
        <TextField
          name="homeAddressLine2"
          placeholder={intl.formatMessage(messages.addressLine2Title)}
          control={control}
        />
        <TextField
          name="homeAddressCity"
          isRequired={true}
          placeholder={intl.formatMessage(messages.addressCityTitle)}
          control={control}
        />
        {countryCode === CountryCode.UnitedStates && (
          <TextField
            name="homeAddressState"
            isRequired={true}
            placeholder={intl.formatMessage(messages.addressStateTitle)}
            control={control}
          />
        )}
        {countryCode !== CountryCode.ArabEmirates && (
          <TextField
            name="homeAddressPostcode"
            isRequired={true}
            placeholder={intl.formatMessage(
              countryCode === CountryCode.UnitedStates
                ? messages.addressZipCodeTitle
                : messages.addressPostcodeTitle,
            )}
            control={control}
          />
        )}

        <DocsList
          title={intl.formatMessage(messages.docsTitle)}
          className={s.docs}
          onChange={onDocListItemClick}
          docs={docs}
          isStatusHidden={true}
        />
      </Section>
      <BottomButtons>
        <Button type="submit" isPending={isSubmitting}>
          <FormattedMessage
            defaultMessage="NEXT"
            id="6jg2Fp"
            description="OnBoarding next button text"
          />
        </Button>
      </BottomButtons>
    </>
  );
};

export { DriverForm };
export type { IDriverViewProps };
